import { useNavigate } from "@tanstack/react-router";
import { TextLink, Heading } from "@hawkins/components";
import { Breadcrumb } from "@hawkins/components";
import { AppLayout } from "../../layout/AppLayout/AppLayout.tsx";
import { ContactUsForm } from "./ContactUsForm.tsx";
import { Loader } from "../../utils/Loader/Loader.tsx";
import { useContactUsFormContent } from "../../../hooks/useContactUsFormContent.ts";
import { useContactUsPageContent } from "../../../hooks/useContactUsPageContent.ts";
import { useGlobalContext } from "../../../utils/GlobalContext.ts";
import { useContactFormSubmission } from "../../../hooks/useContactFormSubmission.ts";
import { SuccessfulFormSubmission } from "../../shared/SuccessfulFormSubmission/index.tsx";
import { ErrorSubmittingForm } from "../../shared/ErrorSubmittingForm/index.tsx";

import styles from "./ContactUsPage.module.scss";

export const ContactUsPage = () => {
  const { localeConfig } = useGlobalContext();
  const { formData, loading: formContentLoading } = useContactUsFormContent();
  const { pageData, loading: pageContentLoading } = useContactUsPageContent();
  const {
    submitGoogleForm,
    isGoogleFormSubmitting,
    isGoogleFormSubmissionSuccess,
    isGoogleFormSubmissionError,
  } = useContactFormSubmission(pageData?.googleFormId);
  const navigate = useNavigate();

  const pageTitle = pageData?.pageTitle ?? "Contact us";
  const pageDescription =
    pageData?.description ??
    "Thanks for reaching out. Please provide your contact details and message, we will respond in 24-28 hours.";
  const submittedRequestTitle =
    pageData?.submittedRequestTitle ?? "Thanks for your message";
  const submittedRequestDescription =
    pageData?.submittedRequestDescription ??
    "A member of our team will respond to your message in 24-48 hours.";
  const backButtonLabel = pageData?.backButtonLabel ?? "Back to home page";

  return (
    <AppLayout>
      <div className={styles.contentWrapper}>
        {isGoogleFormSubmissionError && !pageContentLoading ? (
          <ErrorSubmittingForm />
        ) : (
          <>
            {isGoogleFormSubmissionSuccess &&
            pageData &&
            !pageContentLoading ? (
              <SuccessfulFormSubmission
                title={submittedRequestTitle}
                description={submittedRequestDescription}
                buttonLabel={backButtonLabel}
                onButtonClick={() => {
                  navigate({
                    to: `/${localeConfig?.currentLocale?.toLowerCase() || ""}`,
                  });
                }}
              />
            ) : (
              <>
                {formContentLoading || pageContentLoading ? (
                  <Loader fullBlock={true} text="Loading page content..." />
                ) : (
                  <div className={styles?.contentContainer}>
                    <Breadcrumb>
                      <TextLink
                        href={`/${localeConfig?.currentLocale?.toLowerCase() || ""}`}
                      >
                        {backButtonLabel}
                      </TextLink>
                      <TextLink>{pageTitle}</TextLink>
                    </Breadcrumb>
                    <div className={styles?.content}>
                      <Heading level={5}>{pageTitle}</Heading>
                      <Heading level={0}>{pageDescription}</Heading>
                    </div>
                    <ContactUsForm
                      formData={formData}
                      pageData={pageData}
                      isGoogleFormSubmitting={isGoogleFormSubmitting}
                      submitGoogleForm={submitGoogleForm}
                    />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
};
